/**
 * Stack Ranking tool runner.
 * Aggregates participant rankings using Borda count and consensus scoring.
 */

import type {
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
} from "../harmonizer/types";
import type { NativeScoreSet } from "../harmonizer/testHarness";
import type { RawToolSpec } from "../harmonizer/specLoader";
import { loadToolSpecFile, getToolSpecPath } from "./specLoader";

let cachedSpec: RawToolSpec | null = null;

/**
 * Gets the raw tool spec for Stack Ranking.
 */
export function getStackRankingSpec(): RawToolSpec {
  if (cachedSpec) {
    return cachedSpec;
  }

  const specPath = getToolSpecPath("stack-ranking");
  cachedSpec = loadToolSpecFile(specPath);
  return cachedSpec;
}

/**
 * Runs the Stack Ranking tool.
 * 
 * Note: This implementation assumes participant rankings are stored in toolConfig.
 * In a real system, rankings would be collected through interactive UI.
 * 
 * @param items - Items to rank
 * @param _criteriaSet - CriteriaSet (not used)
 * @param toolConfig - Tool configuration (participant_count, participant_rankings, consensus_algorithm)
 * @param lens - Optional lens for filtering
 * @returns Native score set
 */
export async function runStackRankingTool(
  items: Item[],
  _criteriaSet: CriteriaSet | null,
  toolConfig: ToolConfig,
  lens?: Lens | null
): Promise<NativeScoreSet> {
  const toolRunId = `stack-ranking-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Get config values
  const participantCount = (toolConfig.participant_count as number) || 1;
  const consensusAlgorithm = (toolConfig.consensus_algorithm as string) || "borda_with_consensus";
  const participantRankings = (toolConfig.participant_rankings as Array<{
    participant_id: string;
    participant_name?: string;
    ranking: string[];
  }>) || [];

  // Filter items if lens is provided
  let filteredItems = items;
  if (lens) {
    const { applyLens } = await import("../arrange/validation");
    filteredItems = applyLens(items, lens);
  }

  const n = filteredItems.length;
  const itemIds = new Set(filteredItems.map((item) => item.id));

  // Calculate Borda scores
  const bordaScores = new Map<string, number>();
  const rankPositions = new Map<string, number[]>(); // Track all rank positions for each item

  // Initialize scores
  for (const item of filteredItems) {
    bordaScores.set(item.id, 0);
    rankPositions.set(item.id, []);
  }

  // Process each participant's ranking
  for (const participantRanking of participantRankings) {
    const ranking = participantRanking.ranking || [];

    for (let position = 0; position < ranking.length; position++) {
      const itemId = ranking[position];

      // Only process items that exist in our filtered set
      if (!itemIds.has(itemId)) {
        continue;
      }

      // Borda count: position i (0-indexed) gets (n - i - 1) points
      // For 1-indexed thinking: position 1 gets (n-1) points, position 2 gets (n-2), etc.
      const points = n - position - 1;

      const currentScore = bordaScores.get(itemId) || 0;
      bordaScores.set(itemId, currentScore + points);

      // Track rank position (1-indexed for average calculation)
      const positions = rankPositions.get(itemId) || [];
      positions.push(position + 1);
      rankPositions.set(itemId, positions);
    }
  }

  // Calculate average ranks and consensus scores
  const itemsWithScores: Array<{
    item: Item;
    score: number;
    averageRank: number;
    consensusScore: number;
    standardDeviation: number;
  }> = [];

  // Calculate standard deviations for consensus
  const allStandardDeviations: number[] = [];

  for (const item of filteredItems) {
    const score = bordaScores.get(item.id) || 0;
    const positions = rankPositions.get(item.id) || [];

    let averageRank = 0;
    let standardDeviation = 0;

    if (positions.length > 0) {
      averageRank = positions.reduce((sum, p) => sum + p, 0) / positions.length;

      // Calculate standard deviation
      const variance =
        positions.reduce((sum, p) => sum + Math.pow(p - averageRank, 2), 0) / positions.length;
      standardDeviation = Math.sqrt(variance);
      allStandardDeviations.push(standardDeviation);
    } else {
      // Item not ranked by any participant
      averageRank = n + 1; // Worst possible rank
      standardDeviation = 0;
    }

    itemsWithScores.push({
      item,
      score,
      averageRank,
      consensusScore: 0, // Will calculate after we have max std dev
      standardDeviation,
    });
  }

  // Calculate consensus scores (normalized to 0-1)
  const maxStdDev = allStandardDeviations.length > 0 ? Math.max(...allStandardDeviations) : 1;

  for (const itemScore of itemsWithScores) {
    if (maxStdDev > 0) {
      // Consensus = 1 - (normalized_standard_deviation)
      itemScore.consensusScore = 1 - itemScore.standardDeviation / maxStdDev;
    } else {
      itemScore.consensusScore = 1.0; // Perfect consensus if no variance
    }
  }

  // Sort by score (descending), then by average rank (ascending), then by consensus (descending), then by label
  itemsWithScores.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    if (a.averageRank !== b.averageRank) {
      return a.averageRank - b.averageRank;
    }
    if (b.consensusScore !== a.consensusScore) {
      return b.consensusScore - a.consensusScore;
    }
    return a.item.label.localeCompare(b.item.label);
  });

  // Calculate overall consensus (average of all consensus scores)
  const overallConsensus =
    itemsWithScores.length > 0
      ? itemsWithScores.reduce((sum, item) => sum + item.consensusScore, 0) / itemsWithScores.length
      : 0;

  // Assign ranks and build output
  const scoredItems: Array<{
    item_id: string;
    score: number;
    rank: number;
    average_rank: number;
    consensus_score: number;
    standard_deviation: number;
  }> = [];
  let currentRank = 1;

  for (let i = 0; i < itemsWithScores.length; i++) {
    const { item, score, averageRank, consensusScore, standardDeviation } = itemsWithScores[i];

    // If score differs from previous, update rank
    if (
      i > 0 &&
      (itemsWithScores[i - 1].score !== score ||
        itemsWithScores[i - 1].averageRank !== averageRank ||
        itemsWithScores[i - 1].consensusScore !== consensusScore)
    ) {
      currentRank = i + 1;
    }

    scoredItems.push({
      item_id: item.id,
      score: score,
      rank: currentRank,
      average_rank: averageRank,
      consensus_score: consensusScore,
      standard_deviation: standardDeviation,
    });
  }

  return {
    items: scoredItems,
    toolRunId,
    metadata: {
      total_items: filteredItems.length,
      participant_count: participantRankings.length || participantCount,
      overall_consensus: overallConsensus,
      consensus_algorithm: consensusAlgorithm,
    },
  };
}


